import React from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { useI18n } from '../i18n/I18nProvider';
import {
  TRAINING_MODE_COMPREHENSIVE,
  TRAINING_MODE_MEANING,
  hasTrainingMode,
  normalizeTrainingMode,
  normalizeTrainingModes,
  toggleTrainingMode,
  trainingModeDescriptionKey,
  trainingModeLabelKey,
} from '../lib/trainingModes';

const OPTIONS = [TRAINING_MODE_MEANING, TRAINING_MODE_COMPREHENSIVE];

const modeBadgeClasses = (mode) => (
  mode === TRAINING_MODE_COMPREHENSIVE
    ? 'border-blue-200 bg-blue-50 text-blue-800'
    : 'border-emerald-200 bg-emerald-50 text-emerald-800'
);

export const TrainingModeBadge = ({ mode, className = '' }) => {
  const { t } = useI18n();
  const modes = normalizeTrainingModes(mode);
  return (
    <span className={`inline-flex flex-wrap items-center gap-1 ${className}`}>
      {modes.map((normalized, index) => (
        <React.Fragment key={normalized}>
          {index > 0 && <span className="px-0.5 text-xs font-semibold text-gray-400" aria-hidden="true">+</span>}
          <span className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-xs font-semibold ${modeBadgeClasses(normalized)}`}>
            {normalized === TRAINING_MODE_COMPREHENSIVE && <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />}
            {t(trainingModeLabelKey(normalized))}
          </span>
        </React.Fragment>
      ))}
    </span>
  );
};

const TrainingModeSelector = ({
  value,
  onChange,
  counts,
  showCounts = false,
  disabled = false,
  legend = 'Training mode',
  name = 'training-mode',
  className = '',
}) => {
  const { t } = useI18n();
  const selected = normalizeTrainingModes(value);
  const legacySingleValue = !Array.isArray(value);
  return (
    <fieldset className={className} disabled={disabled}>
      <legend className="text-sm font-semibold text-gray-900">{t(legend)}</legend>
      <div className="mt-2 grid gap-2 sm:grid-cols-2" role="group" aria-label={t(legend)}>
        {OPTIONS.map((mode) => {
          const count = counts?.[mode];
          const numericCount = count == null ? null : Number(count?.count ?? count);
          const unavailable = showCounts && numericCount != null && Number.isFinite(numericCount) && numericCount <= 0;
          const isSelected = hasTrainingMode(selected, mode);
          const lastSelected = isSelected && selected.length === 1;
          const palette = mode === TRAINING_MODE_COMPREHENSIVE
            ? {
              selected: 'border-blue-500 bg-blue-50 ring-1 ring-blue-200',
              hover: 'hover:border-blue-300 hover:bg-blue-50',
              input: 'text-blue-600',
            }
            : {
              selected: 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-200',
              hover: 'hover:border-emerald-300 hover:bg-emerald-50',
              input: 'text-emerald-600',
            };
          const nextValue = toggleTrainingMode(selected, mode);
          return (
            <label
              key={mode}
              data-testid={`training-mode-${mode}`}
              className={`relative flex cursor-pointer items-start gap-3 rounded border p-3 transition-colors ${
                unavailable
                  ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-60'
                  : isSelected
                    ? palette.selected
                    : `border-gray-200 bg-white ${palette.hover}`
              }`}
            >
              <input
                type="checkbox"
                name={name}
                value={mode}
                checked={isSelected}
                disabled={unavailable || lastSelected}
                onChange={() => onChange?.(legacySingleValue ? normalizeTrainingMode(mode) : nextValue)}
                className={`mt-1 h-4 w-4 ${palette.input}`}
              />
              <span className="min-w-0">
                <span className="flex flex-wrap items-center gap-2 font-semibold text-gray-900">
                  {t(trainingModeLabelKey(mode))}
                  {showCounts && count != null && (
                    <span className="rounded bg-white px-1.5 py-0.5 text-xs font-bold tabular-nums text-gray-600">
                      {count}
                    </span>
                  )}
                </span>
                <span className="mt-1 block text-xs leading-5 text-gray-600">
                  {t(trainingModeDescriptionKey(mode))}
                </span>
                {unavailable && (
                  <span className="mt-1 block text-xs font-medium text-gray-500">
                    {t('No meanings are available for this mode.')}
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
};

export default TrainingModeSelector;
